import { useEffect, useMemo, useState } from "react";
import {
  Check,
  Globe2,
  MapPin,
  Star,
  Plus,
  Minus,
  MessageSquare,
  Phone,
  AlertCircle,
  X as CloseIcon,
} from "lucide-react";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import HeaderAssistant from "../../components/HeaderAssistant";
import HeaderDash from "../../components/HeaderDash";
import AssistantBookingModal from "../../components/AssistantBookingModal";
import AssistantBookingReviewModal from "../../components/AssistantBookingReviewModal";
import PaymentMethodModal from "../../components/PaymentMethodModal";
import BankTransferModal from "../../components/BankTransferModal";
import PaymentConfirmedModal from "../../components/PaymentConfirmedModal";
import AuthLoginModal from "../../components/AuthLoginModal";
import { useAuthStore } from "../../hooks/useAuthStore";
import { useToast } from "../../hooks/useToast";
import ServiceRequestModal from "../../components/ServiceRequestModal";
import AcceptSuccessModal from "../../components/AcceptSuccessModal";
import providersService from "../../api/services/providersService";
import assistantsService from "../../api/services/assistantsService";
import bookingService from "../../api/services/bookingService";
import messagesService from "../../api/services/messagesService";
import axiosInstance from "../../api/Axiosinstance";
import { ENDPOINTS } from "../../api/Endpoints";
import {
  deriveAssistantNotIncluded,
  normalizeAssistantServices,
} from "../../data/assistantServiceOptions";
import { deriveBookingAddOns } from "../../data/bookingAddOns";
import { resolveAssistantBookingAvailability } from "../../utils/assistantAvailability";
import {
  DEFAULT_RETURN_BUFFER_MINUTES,
  countBlocks,
  getBlockDiscountRate,
} from "../../utils/bookingBlocks";

// Availability is fetched for the same horizon the booking calendar offers.
const AVAILABILITY_HORIZON_DAYS = 180;
const EMPTY_AVAILABILITY = {
  busy: [],
  returnBufferMinutes: DEFAULT_RETURN_BUFFER_MINUTES,
};

const normalizeLanguages = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value
      .split(",")
      .map((lang) => lang.trim())
      .filter(Boolean);
  }
  if (value && typeof value === "object") {
    return Object.values(value)
      .flat()
      .map((lang) => (typeof lang === "string" ? lang.trim() : ""))
      .filter(Boolean);
  }
  return [];
};

const normalizeImageList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (!item) return null;
        if (typeof item === "string") return item;
        return (
          item.url ||
          item.imageUrl ||
          item.image?.url ||
          item.secure_url ||
          item.path ||
          item.fileUrl ||
          item.filename ||
          null
        );
      })
      .filter(Boolean);
  }
  if (typeof value === "object") {
    return normalizeImageList(
      value.images || value.vehicleImages || value.vehicle_images || value.photos
    );
  }
  return [];
};

const normalizeText = (value) =>
  typeof value === "string" ? value.trim() : "";

const normalizeLocationValue = (value) => {
  if (typeof value === "string") return value.trim();
  if (!value || typeof value !== "object") return "";

  const city =
    normalizeText(value.city) ||
    normalizeText(value.town) ||
    normalizeText(value.localGovernment) ||
    normalizeText(value.local_government);
  const state =
    normalizeText(value.state) ||
    normalizeText(value.region) ||
    normalizeText(value.province) ||
    normalizeText(value.based);
  const label =
    normalizeText(value.label) ||
    normalizeText(value.name) ||
    normalizeText(value.address) ||
    normalizeText(value.formattedAddress) ||
    normalizeText(value.formatted_address);

  if (city && state) return `${city}, ${state}`;
  return label || city || state || "";
};

const resolveAssistantLocation = (...candidates) => {
  for (const candidate of candidates) {
    const resolved = normalizeLocationValue(candidate);
    if (resolved) return resolved;
  }
  return "";
};

const formatCurrency = (value, currency = "₦") => {
  const amount = Number(value || 0);
  if (Number.isNaN(amount) || amount <= 0) return "";
  return `${currency}${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const parseCurrencyValue = (value) => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return 0;
  const normalized = value.replace(/[^0-9.]/g, "");
  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : 0;
};

const getCurrencySymbol = (value) => {
  if (typeof value !== "string") return "";
  const match = value.trim().match(/^[^\d]+/);
  return match ? match[0].trim() : "";
};

const getFullName = (source = {}) =>
  source.fullName ||
  source.name ||
  [source.firstName, source.lastName].filter(Boolean).join(" ").trim();

const getJoinedYear = (source = {}) => {
  const raw =
    source.joinedYear ||
    source.joined_year ||
    source.createdAt ||
    source.created_at ||
    "";
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return String(date.getFullYear());
};

const getBookingsCount = (provider = {}, account = {}) => {
  const raw = provider.bookingsCount ?? account.bookingsCount ?? 0;
  const count = Number(raw);
  return Number.isFinite(count) ? count : 0;
};

const getCompletedBookings = (provider = {}, account = {}) => {
  const raw =
    provider.completedBookings ??
    provider.bookingMetrics?.completedBookings ??
    provider.metrics?.completedBookings ??
    provider.stats?.completedBookings ??
    provider.totalCompletedBookings ??
    provider.total_completed_bookings ??
    account.completedBookings ??
    account.totalCompletedBookings ??
    0;
  const count = Number(raw);
  return Number.isFinite(count) ? count : 0;
};

const formatCount = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0";
  return num.toLocaleString();
};

const resolveHostStatus = (bookingsCount) => {
  if (bookingsCount >= 10000) return "Superhost";
  if (bookingsCount >= 1000) return "Host";
  if (bookingsCount > 0) return "Sub-Host";
  return "";
};

const unwrapResponse = (response) => {
  if (!response) return {};
  return (
    response.data?.data?.profile ||
    response.data?.data?.user ||
    response.data?.data ||
    response.data?.profile ||
    response.data ||
    response.profile ||
    response.data?.items ||
    response
  );
};

const formatBookingDate = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString("en-GB");
};

const normalizeServiceRequestPayload = (payload = {}, fallbackId = "") => {
  const candidate =
    payload?.booking || payload?.data?.booking || payload?.data || payload || {};
  if (!candidate || typeof candidate !== "object") {
    return null;
  }

  const guest =
    candidate?.guest ||
    candidate?.traveler ||
    candidate?.user ||
    candidate?.passenger ||
    {};
  const guestName =
    guest?.fullName ||
    [guest?.firstName, guest?.lastName].filter(Boolean).join(" ").trim() ||
    candidate?.guestName ||
    "Guest";

  const arrivalDate =
    formatBookingDate(
      candidate?.arrivalDate ||
        candidate?.startDate ||
        candidate?.checkInDate ||
        candidate?.fromDate ||
        candidate?.arrival_date
    ) || "";
  const departureDate =
    formatBookingDate(
      candidate?.departureDate ||
        candidate?.endDate ||
        candidate?.checkOutDate ||
        candidate?.toDate ||
        candidate?.departure_date
    ) || "";

  const priceInput =
    candidate?.amountExpected ??
    candidate?.amountToPay;
  const priceNumber =
    typeof priceInput === "number"
      ? priceInput
      : typeof priceInput === "string"
      ? parseCurrencyValue(priceInput)
      : 0;
  const currencySymbol =
    candidate?.currency ||
    (typeof priceInput === "string" ? getCurrencySymbol(priceInput) : "");
  const formattedPrice =
    Number.isFinite(priceNumber) && priceNumber > 0
      ? formatCurrency(priceNumber, currencySymbol || "₦")
      : candidate?.formattedPrice ||
        candidate?.priceDescription ||
        candidate?.price ||
        "₦0.00";

  const pickup =
    candidate?.pickupLocation ||
    candidate?.pickup ||
    candidate?.pickup_address ||
    candidate?.location ||
    "Pickup location TBD";
  const itinerary = candidate?.itinerary || "";
  const note =
    itinerary ||
    candidate?.note ||
    candidate?.notes ||
    candidate?.message ||
    candidate?.specialNote ||
    "No note provided.";

  const id = String(
    candidate?.id ||
      candidate?._id ||
      candidate?.bookingId ||
      fallbackId ||
      ""
  ).trim();

  return {
    id,
    guestName,
    arrivalDate,
    departureDate,
    price: formattedPrice,
    pickup,
    note,
    noteLabel: itinerary ? "Itinerary" : "Additional note",
  };
};

const mapProviderToAssistant = (
  provider = {},
  expertise = {},
  account = {}
) => {
  const providerUser = provider.user || {};
  const resolvedName = getFullName(providerUser) || "";
  const profilePicture = provider.profilePicture?.url || "";
  const highlights = normalizeImageList(
    provider.vehicle?.images ||
      provider.vehicle?.vehicleImages ||
      provider.vehicleImages ||
      provider.vehicle_images ||
      provider.profile?.vehicle?.images ||
      provider.profile?.vehicle?.vehicleImages
  );
  const services = normalizeAssistantServices(provider.services);
  const reviews = Array.isArray(provider.reviews) ? provider.reviews : [];
  const reviewsCount = provider.reviewsCount || 0;
  const rating = provider.rating ?? 0;
  const location = resolveAssistantLocation(
    provider.location,
    provider.profile?.location,
    provider.address,
    provider.state,
    provider.city,
    provider.profile?.state,
    provider.profile?.city,
    provider.address?.state,
    provider.address?.city,
    providerUser.location,
    providerUser.state,
    providerUser.city
  );
  const notIncluded = deriveAssistantNotIncluded(services);
  const languages = Array.isArray(expertise.languages)
    ? expertise.languages
    : [];
  const specializations = Array.isArray(expertise.specializations)
    ? expertise.specializations
    : [];
  const experienceYears = expertise.experienceYears ?? null;
  const rawPrice = provider.pricePerDay ?? 0;
  const pricePerDay = Number(rawPrice);
  const resolvedPricePerDay =
    Number.isFinite(pricePerDay) && pricePerDay > 0 ? pricePerDay : 0;
  const currency =
    provider.wallet?.currency === "NGN" ? "₦" : provider.wallet?.currency || "";
  const joinedYear = getJoinedYear(provider) || "";
  const bookingsCount = getBookingsCount(provider, account);
  const completedBookings = getCompletedBookings(provider, account);
  const hostStatus = resolveHostStatus(bookingsCount);
  const expectations = provider.expectations || "";

  return {
    id: provider.id || "",
    name: resolvedName,
    bio: provider.bio || "",
    about: provider.bio || "",
    servicesOffered: services,
    notIncluded,
    languages,
    location,
    image: profilePicture || "",
    highlights,
    price: formatCurrency(resolvedPricePerDay, currency),
    pricePerDay: resolvedPricePerDay,
    walletBalance: formatCurrency(provider.wallet?.balance, currency),
    expectations,
    experienceYears,
    joinedYear,
    specializations,
    bookingsCount,
    completedBookings,
    hostStatus,
    isAvailable: resolveAssistantBookingAvailability(provider),
    reviews,
    reviewsCount,
    rating,
  };
};

const mapAssistantApiToProfile = (assistant = {}) => {
  const user = assistant.user || {};
  const resolvedName = getFullName(user) || "";
  const profilePicture = assistant.profilePicture?.url || "";
  const highlights = normalizeImageList(
    assistant.highlights ||
      assistant.vehicle?.images ||
      assistant.vehicle?.vehicleImages ||
      assistant.vehicleImages ||
      assistant.vehicle_images ||
      assistant.profile?.vehicle?.images ||
      assistant.profile?.vehicle?.vehicleImages
  );
  const reviews = Array.isArray(assistant.reviews) ? assistant.reviews : [];
  const reviewsCount = assistant.reviewsCount || 0;
  const rating = assistant.rating ?? 0;
  const languages = normalizeLanguages(assistant.languages || []);
  const experienceYears = assistant.experienceYears ?? null;
  const rawPrice = assistant.pricePerDay ?? 0;
  const pricePerDay = Number(rawPrice);
  const resolvedPricePerDay =
    Number.isFinite(pricePerDay) && pricePerDay > 0 ? pricePerDay : 0;
  const currency =
    assistant.currency === "NGN" ? "₦" : assistant.currency || "";
  const joinedYear = getJoinedYear(assistant) || "";
  const bookingsCount = getBookingsCount(assistant, user);
  const completedBookings = getCompletedBookings(assistant, user);
  const hostStatus = resolveHostStatus(bookingsCount);

  return {
    id: user._id || "",
    name: resolvedName,
    bio: assistant.bio || "",
    about: assistant.bio || "",
    servicesOffered: normalizeAssistantServices(assistant.services),
    notIncluded: deriveAssistantNotIncluded(assistant.services),
    languages,
    location: resolveAssistantLocation(
      assistant.location,
      assistant.profile?.location,
      assistant.address,
      assistant.state,
      assistant.city,
      assistant.profile?.state,
      assistant.profile?.city,
      assistant.address?.state,
      assistant.address?.city,
      user.location,
      user.state,
      user.city
    ),
    image: profilePicture || "",
    highlights,
    price: formatCurrency(resolvedPricePerDay, currency),
    pricePerDay: resolvedPricePerDay,
    walletBalance: formatCurrency(assistant.wallet?.balance, currency),
    expectations: assistant.expectations || "",
    experienceYears,
    joinedYear,
    specializations: assistant.specializations || [],
    bookingsCount,
    completedBookings,
    hostStatus,
    isAvailable: resolveAssistantBookingAvailability(assistant),
    reviews,
    reviewsCount,
    rating,
    contactEmail: user.email || "",
    contactPhone: user.phone || "",
    ...deriveBookingAddOns(assistant),
  };
};

const mapAssistantSummary = (assistant = {}) => {
  const mapped = mapAssistantApiToProfile(assistant);
  return {
    id: mapped.id,
    name: mapped.name,
    completedBookings: mapped.completedBookings,
    location: mapped.location,
    languages: normalizeLanguages(mapped.languages).join(", "),
    image: mapped.image,
  };
};

function SectionTitle({ children, className = "" }) {
  return (
    <h2
      className={`text-[14px] sm:text-xl font-semibold text-[#570033] flex items-center gap-2 ${className}`}
    >
      {children}
    </h2>
  );
}

function Profile() {
  const { id, requestId } = useParams();
  const { state } = useLocation();
  const locationServiceRequest = state?.serviceRequest;
  const navigate = useNavigate();
  const [providerProfile, setProviderProfile] = useState(null);
  const [assistantRemote, setAssistantRemote] = useState(null);
  const [assistantNotFound, setAssistantNotFound] = useState(false);
  const [relatedAssistants, setRelatedAssistants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const { isAuthenticated, user, role } = useAuthStore();
  const toast = useToast();

  const renderHeader = () => {
    if (!isAuthenticated) return <Header />;
    if (role === "assistant" || role === "serviceProvider") return <HeaderAssistant />;
    return <HeaderDash />;
  };

  const fetchProviderData = async () => {
    const storedUser = user;

    const [profileResult, expertiseResult, accountResult] =
      await Promise.allSettled([
        providersService.getProfile(),
        providersService.getExpertise(),
        axiosInstance.get(ENDPOINTS.AUTH.ACCOUNT),
      ]);
    const profileResponse =
      profileResult.status === "fulfilled" ? profileResult.value : null;
    const expertiseResponse =
      expertiseResult.status === "fulfilled" ? expertiseResult.value : null;
    const accountResponse =
      accountResult.status === "fulfilled" ? accountResult.value : null;
    const profile = unwrapResponse(profileResponse);
    const expertise = unwrapResponse(expertiseResponse);
    const account = unwrapResponse(accountResponse);

    return mapProviderToAssistant(
      profile || {},
      expertise || {},
      account || storedUser || {}
    );
  };

  const assistant = useMemo(() => {
    const baseAssistant = {
      id: "",
      name: "",
      date: "",
      rating: 0,
      reviewsCount: 0,
      location: "",
      languages: [],
      price: "",
      pricePerDay: null,
      durationLabel: "",
      discount: "",
      image: "",
      contactEmail: "",
      contactPhone: "",
      hostStatus: "",
      experienceYears: null,
      bio: "",
      about: "",
      expectations: "",
      servicesOffered: [],
      notIncluded: [],
      highlights: [],
      reviews: [],
      bookingsCount: 0,
      completedBookings: 0,
      faqs: [],
      joinedYear: "",
      specializations: [],
      walletBalance: "",
      isAvailable: null,
      escortVehicles: [],
      securityPersonnel: [],
    };
    const incoming = state?.assistant || {};
    if (id && assistantRemote) {
      return {
        ...baseAssistant,
        ...incoming,
        ...assistantRemote,
        id: assistantRemote.id || id || incoming.id || "",
      };
    }
    if (id) {
      return {
        ...baseAssistant,
        ...incoming,
        id: id || incoming.id || "",
      };
    }
    return providerProfile
      ? { ...baseAssistant, ...providerProfile }
      : {
          ...baseAssistant,
          ...incoming,
          id: incoming.id || "",
        };
  }, [id, state, providerProfile, assistantRemote]);

  useEffect(() => {
    if (id) return;
    let active = true;

    const fetchProviderProfile = async () => {
      try {
        setIsLoading(true);
        const mappedProfile = await fetchProviderData();
        if (!active) return;
        setProviderProfile(mappedProfile);
      } catch (error) {
        // Keep default profile data on failure.
      } finally {
        if (active) setIsLoading(false);
      }
    };

    fetchProviderProfile();
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let active = true;

    const fetchAssistant = async () => {
      setIsLoading(true);
      setAssistantNotFound(false);
      setAssistantRemote(null);
      const [assistantResult, relatedResult] = await Promise.allSettled([
        assistantsService.getAssistantById(id),
        assistantsService.getRelatedAssistants(id),
      ]);

      if (!active) return;

      if (assistantResult.status === "fulfilled") {
        const assistantData = unwrapResponse(assistantResult.value);
        setAssistantRemote(mapAssistantApiToProfile(assistantData));
      } else {
        setAssistantNotFound(true);
      }

      if (relatedResult.status === "fulfilled") {
        const relatedData = unwrapResponse(relatedResult.value);
        const results =
          relatedData?.results ||
          relatedData?.assistants ||
          relatedData?.data?.results ||
          relatedData?.data ||
          relatedData?.items ||
          relatedData;
        const list = Array.isArray(results) ? results : [];
        setRelatedAssistants(list.map(mapAssistantSummary));
      }

      if (active) setIsLoading(false);
    };

    fetchAssistant();
    return () => {
      active = false;
    };
  }, [id]);

  const languageList = useMemo(() => {
    const normalized = normalizeLanguages(assistant.languages);
    if (normalized.length > 0) return normalized;
    return [];
  }, [assistant.languages]);
  const joinedMeta = useMemo(() => {
    const items = [];
    if (assistant.joinedYear) items.push(`Joined in ${assistant.joinedYear}`);
    if (assistant.experienceYears)
      items.push(`${assistant.experienceYears} years experience`);
    return items.length ? items.join(" • ") : "";
  }, [assistant.joinedYear, assistant.experienceYears]);

  const [serviceRequestDetails, setServiceRequestDetails] = useState(null);
  const [serviceRequestLoading, setServiceRequestLoading] = useState(false);
  // Only whether the load failed — the reason goes out as a toast.
  const [serviceRequestFailed, setServiceRequestFailed] = useState(false);
  const [
    serviceRequestActionLoading,
    setServiceRequestActionLoading,
  ] = useState(false);
  const [serviceRequestActionTarget, setServiceRequestActionTarget] =
    useState(null);
  const [showAcceptSuccess, setShowAcceptSuccess] = useState(false);

  useEffect(() => {
    if (!requestId) {
      setServiceRequestDetails(null);
      setServiceRequestFailed(false);
      setServiceRequestLoading(false);
      setServiceRequestActionTarget(null);
      setServiceRequestActionLoading(false);
      setShowAcceptSuccess(false);
      return;
    }

    let active = true;
    const cachedRequest =
      locationServiceRequest?.id === requestId ? locationServiceRequest : null;

    setServiceRequestLoading(true);
    setServiceRequestFailed(false);
    setServiceRequestDetails(null);
    setServiceRequestActionTarget(null);
    setShowAcceptSuccess(false);
    setServiceRequestActionLoading(false);

    if (cachedRequest) {
      const normalized = normalizeServiceRequestPayload(
        cachedRequest,
        requestId
      );
      setServiceRequestDetails(normalized);
      setServiceRequestLoading(false);
      return () => {
        active = false;
      };
    }

    bookingService
      .getBookingById(requestId)
      .then((response) => {
        if (!active) return;
        const normalized = normalizeServiceRequestPayload(response, requestId);
        if (normalized) {
          setServiceRequestDetails(normalized);
          setServiceRequestFailed(false);
        } else {
          setServiceRequestDetails(null);
          setServiceRequestFailed(true);
          toast.error("Booking data is malformed.");
        }
      })
      .catch((error) => {
        if (!active) return;
        setServiceRequestDetails(null);
        setServiceRequestFailed(true);
        toast.apiError(error, "Unable to load the service request.");
      })
      .finally(() => {
        if (active) {
          setServiceRequestLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [requestId, locationServiceRequest, toast]);

  const isServiceRequestModalOpen = Boolean(requestId);

  const closeServiceRequestModal = () => {
    setShowAcceptSuccess(false);
    setServiceRequestActionTarget(null);
    setServiceRequestActionLoading(false);
    navigate("/dashboards/assistant/profile", { replace: true });
  };

  const handleServiceRequestAction = async (action) => {
    if (!serviceRequestDetails?.id) return;
    setServiceRequestActionLoading(true);
    setServiceRequestActionTarget(action);

    try {
      if (action === "accepted") {
        await bookingService.acceptBooking(serviceRequestDetails.id);
        setShowAcceptSuccess(true);
      } else {
        await bookingService.rejectBooking(serviceRequestDetails.id);
        closeServiceRequestModal();
      }
    } catch (error) {
      toast.apiError(error, "Unable to process this request.");
    } finally {
      setServiceRequestActionLoading(false);
      setServiceRequestActionTarget(null);
    }
  };

  const handleAcceptDone = () => {
    setShowAcceptSuccess(false);
    closeServiceRequestModal();
  };

  const [selectedDuration, setSelectedDuration] = useState("custom");
  const [selectedDays, setSelectedDays] = useState(4);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isPaymentMethodOpen, setIsPaymentMethodOpen] = useState(false);
  const [isBankTransferOpen, setIsBankTransferOpen] = useState(false);
  const [isPaymentConfirmedOpen, setIsPaymentConfirmedOpen] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [bookingDraft, setBookingDraft] = useState(null);
  const [bankTransferData, setBankTransferData] = useState(null);
  const [isMessaging, setIsMessaging] = useState(false);
  // An assistant is never "booked" as a whole — only particular hours are
  // taken. The calendar in the booking modal blocks those; the button below
  // stays live so a user can book any window she still has free.
  const [availability, setAvailability] = useState(EMPTY_AVAILABILITY);
  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");
  // Bumped after a 409 to pull a fresh calendar.
  const [availabilityNonce, setAvailabilityNonce] = useState(0);

  useEffect(() => {
    if (!assistant.id) {
      setAvailability(EMPTY_AVAILABILITY);
      return;
    }

    let active = true;
    setIsAvailabilityLoading(true);
    setAvailabilityError("");

    const from = new Date();
    const to = new Date(from.getTime() + AVAILABILITY_HORIZON_DAYS * 24 * 60 * 60 * 1000);

    assistantsService
      .getAssistantAvailability(assistant.id, { from, to })
      .then((response) => {
        if (!active) return;
        const data = response?.data || response || {};
        setAvailability({
          busy: Array.isArray(data.busy) ? data.busy : [],
          returnBufferMinutes:
            typeof data.returnBufferMinutes === "number"
              ? data.returnBufferMinutes
              : DEFAULT_RETURN_BUFFER_MINUTES,
        });
      })
      .catch(() => {
        if (!active) return;
        // Without a calendar we can't grey out taken hours. Let the user
        // proceed — the API still refuses a clashing window — but say so.
        setAvailability(EMPTY_AVAILABILITY);
        setAvailabilityError(
          "We couldn't load this assistant's calendar. Your selected time will be confirmed at checkout."
        );
      })
      .finally(() => {
        if (active) setIsAvailabilityLoading(false);
      });

    return () => {
      active = false;
    };
  }, [assistant.id, availabilityNonce]);

  const handlePaymentSuccess = () => {
    setIsPaymentMethodOpen(false);
    setIsBankTransferOpen(false);
    // The window just became unavailable to everyone, including this user if
    // they book again — refetch so the calendar shows it as taken.
    setAvailabilityNonce((prev) => prev + 1);
    setIsPaymentConfirmedOpen(true);
  };

  const handleMessageAssistant = async () => {
    if (!isAuthenticated) {
      setIsAuthOpen(true);
      return;
    }
    const recipientId = assistant.id;
    if (!recipientId) return;
    setIsMessaging(true);
    try {
      const response = await messagesService.sendMessage(
        recipientId,
        "Hi, I just completed my booking and would like to discuss the itinerary."
      );
      const conversationId = response?.data?.conversationId;
      setIsPaymentConfirmedOpen(false);
      navigate("/user/messages", {
        state: {
          conversationId,
          recipientId,
          assistantId: recipientId,
          assistantName: assistant.name,
          assistantImage: assistant.image,
        },
      });
    } catch (error) {
      toast.apiError(error, "Unable to start conversation.");
    } finally {
      setIsMessaging(false);
    }
  };

  const durationOptions = [
    { id: "day", label: "1 Day", days: 1 },
    { id: "week", label: "1 Week", days: 7 },
    { id: "month", label: "1 Month", days: 30 },
    { id: "custom", label: "Custom", days: selectedDays },
  ];

  const currencySymbol = useMemo(
    () => getCurrencySymbol(assistant.price),
    [assistant.price]
  );
  // The advertised rate has always been per 12-hour block ("/12hrs"), and the
  // API now bills it that way too.
  const pricePerBlock = useMemo(() => {
    const value = Number(assistant.pricePerDay);
    if (Number.isFinite(value) && value > 0) return value;
    return parseCurrencyValue(assistant.price);
  }, [assistant.price, assistant.pricePerDay]);
  const bookingBlocks = useMemo(() => {
    if (bookingDraft?.blocks) return Number(bookingDraft.blocks);
    if (bookingDraft?.arrivalDate && bookingDraft?.departureDate) {
      return countBlocks(
        new Date(bookingDraft.arrivalDate),
        new Date(bookingDraft.departureDate)
      );
    }
    // Before a window is chosen, the quick-pick tabs quote in whole days.
    return Math.max(1, selectedDays * 2);
  }, [bookingDraft, selectedDays]);

  const discountRate = useMemo(
    () => getBlockDiscountRate(bookingBlocks),
    [bookingBlocks]
  );
  const baseTotalAmount = useMemo(
    () => pricePerBlock * bookingBlocks,
    [pricePerBlock, bookingBlocks]
  );
  const totalPrice = useMemo(
    () => formatCurrency(baseTotalAmount * discountRate, currencySymbol || "₦"),
    [baseTotalAmount, discountRate, currencySymbol]
  );
  const totalAmount = useMemo(
    () => Number((baseTotalAmount * discountRate).toFixed(2)),
    [baseTotalAmount, discountRate]
  );
  const discountPercent = useMemo(
    () => Math.round((1 - discountRate) * 100),
    [discountRate]
  );
  // Add-on selections (escort vehicles + security personnel) come from the
  // booking modal and roll into the total carried to review/payment.
  const addOnsTotal = useMemo(
    () => Number(bookingDraft?.addOnsTotal) || 0,
    [bookingDraft]
  );
  const grandTotalAmount = useMemo(
    () => Number((totalAmount + addOnsTotal).toFixed(2)),
    [totalAmount, addOnsTotal]
  );
  const grandTotalPrice = useMemo(
    () => formatCurrency(grandTotalAmount, currencySymbol || "₦"),
    [grandTotalAmount, currencySymbol]
  );
  const firstName = assistant.name?.split(" ").filter(Boolean)[0] || "";

  const userEmail = useMemo(() => {
    return user?.email || "";
  }, [user]);

  const handleDurationSelect = (option) => {
    setSelectedDuration(option.id);
    if (option.days) {
      setSelectedDays(option.days);
    }
  };

  const adjustDays = (delta) => {
    setSelectedDays((prev) => {
      const next = Math.max(1, prev + delta);
      return next;
    });
    setSelectedDuration("custom");
  };

  if (isLoading) {
    return (
      <div className="bg-white min-h-screen">
        {renderHeader()}
        <main className="mx-auto w-full max-w-[1200px] px-4 pb-20 pt-6 sm:px-6 lg:px-0">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.1fr_1fr] lg:items-start">
            <div className="space-y-8">
              <div className="animate-pulse rounded-2xl border border-[#F0D9E7] bg-white p-6 shadow-sm">
                <div className="mx-auto h-24 w-24 rounded-full bg-gray-200" />
                <div className="mt-4 space-y-3">
                  <div className="mx-auto h-4 w-40 rounded bg-gray-200" />
                  <div className="mx-auto h-3 w-32 rounded bg-gray-200" />
                  <div className="mx-auto h-3 w-44 rounded bg-gray-200" />
                </div>
                <div className="mt-6 grid grid-cols-[1fr_auto] gap-3">
                  <div className="h-11 rounded-lg bg-gray-200" />
                  <div className="h-11 w-11 rounded-lg bg-gray-200" />
                </div>
                <div className="mt-4 h-11 rounded-lg bg-gray-200" />
              </div>

              <div className="animate-pulse space-y-3">
                <div className="h-4 w-28 rounded bg-gray-200" />
                <div className="h-20 w-full rounded bg-gray-200" />
              </div>

              <div className="animate-pulse space-y-3">
                <div className="h-4 w-24 rounded bg-gray-200" />
                <div className="space-y-2">
                  <div className="h-3 w-40 rounded bg-gray-200" />
                  <div className="h-3 w-36 rounded bg-gray-200" />
                  <div className="h-3 w-44 rounded bg-gray-200" />
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="animate-pulse rounded-2xl border border-[#F0D9E7] bg-white p-6 shadow-sm">
                <div className="h-4 w-40 rounded bg-gray-200" />
                <div className="mt-4 flex flex-wrap gap-2">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div
                      key={`duration-skeleton-${index}`}
                      className="h-8 w-20 rounded bg-gray-200"
                    />
                  ))}
                </div>
                <div className="mt-5 h-28 rounded-2xl bg-gray-200" />
                <div className="mt-4 h-6 rounded bg-gray-200" />
              </div>

              <div className="animate-pulse space-y-3">
                <div className="h-4 w-36 rounded bg-gray-200" />
                <div className="grid gap-3 sm:grid-cols-2">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div
                      key={`card-skeleton-${index}`}
                      className="h-32 rounded-xl bg-gray-200"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (assistantNotFound || !assistant || !assistant.id) {
    return (
      <div className="bg-white min-h-screen flex flex-col">
        {renderHeader()}
        <main className="flex-1 flex items-center justify-center p-6">
          <div className="text-center space-y-4">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto" />
            <h2 className="text-2xl font-bold text-[#570033]">Assistant Not Found</h2>
            <p className="text-gray-600">The assistant you are looking for is no longer available or does not exist.</p>
            <button
              onClick={() => navigate("/")}
              className="mt-4 px-6 py-2 bg-[#570033] text-white rounded-lg hover:bg-[#570033]/90 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      {renderHeader()}
      <main className="mx-auto w-full max-w-[1200px] px-4 pb-20 pt-6 sm:px-6 lg:px-0">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.1fr_1fr] lg:items-start">
          <div className="space-y-10">
            <section className="rounded-2xl bg-white p-6 text-center shadow-sm">
              <div className="flex flex-col items-center gap-4">
                <div className="w-28 h-28 rounded-full overflow-hidden border-2 border-[#570033]/20 shadow-sm">
                  <img
                    src={assistant.image}
                    alt={assistant.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="space-y-1">
                  <h1 className="text-2xl font-bold text-[#570033] leading-tight">
                    {assistant.name}
                  </h1>
                  {joinedMeta ? (
                    <p className="text-sm text-slate-600">{joinedMeta}</p>
                  ) : null}
                  {/* Not in design — location row hidden
                  <div className="flex items-center justify-center gap-1 text-sm text-slate-600">
                    <MapPin className="h-4 w-4 text-slate-500" />
                    <span>{assistant.location || "Location unavailable"}</span>
                  </div>
                  */}
                  <p className="text-sm text-slate-600">
                    {formatCount(assistant.completedBookings)} completed bookings
                    {assistant.hostStatus ? ` • ${assistant.hostStatus}` : ""}
                  </p>
                </div>
              </div>

              <div className="mt-6 w-full">
                <button
                  type="button"
                  onClick={() => {
                    if (!isAuthenticated) {
                      setIsAuthOpen(true);
                      return;
                    }
                    if (assistant.isAvailable === false) return;
                    setIsBookingOpen(true);
                  }}
                  disabled={assistant.isAvailable === false}
                  className="w-full rounded-lg bg-[#570033] px-4 py-3 text-center text-sm font-semibold text-white transition hover:bg-[#450029] disabled:cursor-not-allowed disabled:bg-[#A47A92]"
                >
                  {assistant.isAvailable === false ? "Offline" : "Book Now"}
                </button>
                {/* Not in design — message-assistant button hidden
                <button
                  type="button"
                  onClick={handleMessageAssistant}
                  disabled={isMessaging}
                  className="flex h-11 w-11 items-center justify-center rounded-lg border border-[#E7C0D6] bg-[#F8EEF4] text-[#570033] transition hover:bg-[#F2E4ED] disabled:cursor-not-allowed disabled:opacity-60"
                  aria-label="Message assistant"
                >
                  {isMessaging ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#570033] border-t-transparent" />
                  ) : (
                    <MessageSquare className="h-5 w-5" />
                  )}
                </button>
                */}
              </div>

              <div className="mt-6 w-full text-left">
                <h2 className="text-lg font-bold text-[#570033]">
                  Explore {firstName ? `with ${firstName}` : ""}
                </h2>
                <div className="mt-3 rounded-2xl border border-[#E7C0D6] p-4 text-center">
                  <p className="text-lg font-semibold text-[#570033]">
                    Price: {assistant.price}
                    <span className="text-sm font-semibold text-[#570033]">
                      /12hrs
                    </span>
                  </p>
                </div>
              </div>

              {/* Not in design — Contact info toggle hidden
              <button
                type="button"
                onClick={() => setShowContactInfo((prev) => !prev)}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-[#E7C0D6] px-4 py-3 text-sm font-semibold text-[#570033] transition hover:bg-[#F8EEF4]"
              >
                <Phone className="h-4 w-4" />
                Contact info
                <span className="ml-1 text-xs font-semibold underline">
                  {showContactInfo ? "Hide" : "Show"}
                </span>
              </button>
              {showContactInfo ? (
                <div className="mt-3 space-y-1 text-sm text-slate-600">
                  <p>{assistant.contactPhone}</p>
                  <p>{assistant.contactEmail}</p>
                </div>
              ) : null}
              */}
            </section>

            <section className="space-y-3">
              <h3 className="text-lg font-bold text-[#570033]">About</h3>
              <p className="text-sm text-slate-700 font-medium leading-relaxed">
                {assistant.bio}
              </p>
            </section>

            <section className="space-y-3">
              <h3 className="text-lg font-bold text-[#570033]">Languages</h3>
              <ul className="space-y-2 text-sm text-slate-700 list-disc list-inside">
                {languageList.map((lang) => (
                  <li key={lang}>{lang}</li>
                ))}
              </ul>
            </section>

            <section className="space-y-6">
              <div className="flex justify-between items-center gap-1">
                <h3 className="text-lg font-bold text-[#570033]">Reviews</h3>
                <div className="text-sm text-slate-700 font-medium">
                  {formatCount(assistant.completedBookings)} completed bookings
                </div>
              </div>
              <hr />
              <div className="space-y-4">
                {assistant.reviews.length ? (
                  assistant.reviews.map((review, idx) => (
                    <div key={idx} className="flex gap-3 items-start">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                        {review.avatar ? (
                          <img
                            src={review.avatar}
                            alt={review.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-xs text-slate-500">
                            N/A
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-slate-900">
                          {review.name}
                        </div>
                        <div className="flex items-center font-medium gap-2 text-xs text-slate-700">
                          <span>{review.date}</span>
                          <span className="w-px h-3 bg-slate-300"></span>
                          <span className="flex items-center gap-1 text-slate-600 font-medium">
                            {review.rating}
                            <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                          </span>
                        </div>
                        <p className="text-xs text-slate-700 font-medium leading-relaxed">
                          {review.comment}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-600">
                    No reviews yet. Be the first to leave one.
                  </p>
                )}
              </div>
            </section>

          </div>

          <div className="space-y-10">
            <section className="space-y-3">
              <h3 className="text-lg font-bold text-[#570033]">Expectations</h3>
              {assistant.specializations?.length ? (
                <ul className="space-y-2 text-sm text-slate-800">
                  {assistant.specializations.map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#570033]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-700 font-medium leading-relaxed">
                  {assistant.expectations}
                </p>
              )}
            </section>

            <section className="grid gap-6 sm:grid-cols-2">
              <div className="space-y-3">
                <h3 className="text-lg font-bold text-[#570033]">
                  Services Offered
                </h3>
                <ul className="space-y-2 text-sm text-slate-800">
                  {assistant.servicesOffered.map((service) => (
                    <li key={service} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#570033]" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-3">
                <h3 className="text-lg font-bold text-[#570033]">
                  Not Included
                </h3>
                <ul className="space-y-2 text-sm text-slate-800">
                  {assistant.notIncluded.map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <CloseIcon className="w-4 h-4 text-[#b42318]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="text-lg font-bold text-[#570033]">
                Vehicle Images
              </h3>
              {assistant.highlights.length ? (
                <div className="grid grid-cols-3 gap-3">
                  {assistant.highlights.map((src, idx) => (
                    <div key={idx} className="h-24 rounded-xl overflow-hidden">
                      <img
                        src={src}
                        alt={`Vehicle ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-600">
                  No vehicle images available.
                </p>
              )}
            </section>
          </div>
        </div>

        <section className="mt-14 space-y-3">
          <div className="flex items-center justify-between">
            <SectionTitle className="text-sm">
              Pulo Assistants within Abuja
            </SectionTitle>
            <NavLink
              to="/travel-assistant"
              className="text-xs sm:text-sm font-semibold text-[#570033]"
            >
              See more
            </NavLink>
          </div>
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4 sm:overflow-visible [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {relatedAssistants.length > 0 ? (
              relatedAssistants.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm p-[6px] flex-shrink-0 w-[85%] sm:w-auto sm:flex-shrink snap-center"
                >
                  <div className="relative">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-60 object-cover object-top rounded-xl"
                    />
                  </div>

                  <div className="pt-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <p className="text-base font-semibold text-black">
                          {item.name}
                        </p>
                        <div className="text-sm font-medium text-black">
                          {formatCount(item.completedBookings)} completed bookings
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm font-medium text-black">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4 text-black" />
                        <span>{item.location || "Location unavailable"}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-1">
                          <Globe2 className="w-4 h-4 text-black" />
                          <span>{item.languages}</span>
                        </div>
                        <NavLink
                          to={item.id ? `/assistant/${item.id}` : "/travel-assistant"}
                          className="text-sm font-semibold text-[#570033] underline underline-offset-2"
                        >
                          View
                        </NavLink>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-600">
                No related assistants found.
              </p>
            )}
          </div>
        </section>
      </main>

      <AssistantBookingModal
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        onProceed={(details) => {
          setBookingDraft(details);
          setIsBookingOpen(false);
          setIsReviewOpen(true);
        }}
        escortVehicles={assistant.escortVehicles}
        securityPersonnel={assistant.securityPersonnel}
        currencySymbol={currencySymbol || "₦"}
        assistantFirstName={firstName}
        pricePerBlock={pricePerBlock}
        busy={availability.busy}
        returnBufferMinutes={availability.returnBufferMinutes}
        isAvailabilityLoading={isAvailabilityLoading}
        availabilityError={availabilityError}
      />
      <AssistantBookingReviewModal
        isOpen={isReviewOpen}
        onClose={() => setIsReviewOpen(false)}
        onBack={() => {
          setIsReviewOpen(false);
          setIsBookingOpen(true);
        }}
        onProceed={() => {
          setIsReviewOpen(false);
          setIsPaymentMethodOpen(true);
        }}
        bookingDetails={bookingDraft}
        assistantName={assistant.name}
        baseAmount={totalAmount}
        totalAmount={grandTotalAmount}
        currencySymbol={currencySymbol || "₦"}
      />
      <PaymentMethodModal
        isOpen={isPaymentMethodOpen}
        onClose={() => setIsPaymentMethodOpen(false)}
        onBack={() => {
          setIsPaymentMethodOpen(false);
          setIsReviewOpen(true);
        }}
        onBankTransferReady={({ account, bookingId, reference }) => {
          setBankTransferData({ account, bookingId, reference });
          setIsPaymentMethodOpen(false);
          setIsBankTransferOpen(true);
        }}
        onPaymentSuccess={handlePaymentSuccess}
        bookingDetails={bookingDraft}
        assistantId={assistant.id}
        assistantName={assistant.name}
        totalPrice={grandTotalPrice}
        userEmail={userEmail}
        onWindowConflict={() => {
          // Someone took these hours mid-checkout. Pull a fresh calendar and
          // drop the user back on it to choose again.
          setAvailabilityNonce((prev) => prev + 1);
          setBookingDraft(null);
          setIsPaymentMethodOpen(false);
          setIsReviewOpen(false);
          setIsBookingOpen(true);
        }}
      />
      <BankTransferModal
        isOpen={isBankTransferOpen}
        onClose={() => setIsBankTransferOpen(false)}
        onChangeMethod={() => {
          setIsBankTransferOpen(false);
          setIsPaymentMethodOpen(true);
        }}
        onPaymentSuccess={handlePaymentSuccess}
        totalPrice={grandTotalPrice}
        account={bankTransferData?.account}
        bookingId={bankTransferData?.bookingId}
        reference={bankTransferData?.reference}
      />
      <PaymentConfirmedModal
        isOpen={isPaymentConfirmedOpen}
        onClose={() => setIsPaymentConfirmedOpen(false)}
        onMessage={handleMessageAssistant}
        messageLabel="Message assistant"
        isMessaging={isMessaging}
        onDone={() => setIsPaymentConfirmedOpen(false)}
      />
      <AuthLoginModal
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
        onSuccess={() => {
          setIsAuthOpen(false);
          setIsBookingOpen(true);
        }}
      />
      <ServiceRequestModal
        isOpen={isServiceRequestModalOpen}
        isLoading={serviceRequestLoading}
        hasLoadError={serviceRequestFailed}
        request={serviceRequestDetails}
        onClose={closeServiceRequestModal}
        onAction={handleServiceRequestAction}
        actionLoading={serviceRequestActionLoading}
        actionTarget={serviceRequestActionTarget}
      />
      <AcceptSuccessModal
        isOpen={showAcceptSuccess}
        guestName={serviceRequestDetails?.guestName}
        onDone={handleAcceptDone}
      />

      <Footer />
    </div>
  );
}

export default Profile;
